import React from 'react';
import Quill from 'quill';

interface QuillHtmlRendererProps {
  content: string | any; // Quill Delta content (JSON string or Delta object) or HTML string
  className?: string;
  style?: React.CSSProperties;
  showRawHtml?: boolean;
  onHtmlGenerated?: (html: string) => void;
}

const QuillHtmlRenderer: React.FC<QuillHtmlRendererProps> = ({ 
  content, 
  className = '', 
  style = {},
  showRawHtml = false,
  onHtmlGenerated
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [htmlContent, setHtmlContent] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!content) {
      setHtmlContent('');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      let html: string;

      // Check if content is already HTML
      if (typeof content === 'string' && content.includes('<') && content.includes('>')) {
        // Content is already HTML, use it directly
        html = content;
        console.log('Content is HTML, using directly:', html);
      } else {
        // Content is Delta format, convert to HTML
        console.log('Content is Delta, converting to HTML:', content);
        
        // Create a temporary Quill instance to convert Delta to HTML
        const tempContainer = document.createElement('div');
        const quill = new Quill(tempContainer, {
          readOnly: true,
          theme: 'snow',
          modules: {
            toolbar: false, // Disable toolbar for rendering
            clipboard: {
              matchVisual: false
            }
          }
        });

        // Set the content
        let delta;
        if (typeof content === 'string') {
          try {
            delta = JSON.parse(content);
          } catch (parseError) {
            throw new Error('Invalid JSON content');
          }
        } else {
          delta = content;
        }

        // Set the content to the Quill instance
        quill.setContents(delta);

        // Get the HTML content from the Quill root
        html = quill.root.innerHTML;
      }

      setHtmlContent(html);
      
      // Call the callback if provided
      if (onHtmlGenerated) {
        onHtmlGenerated(html);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to render content');
      console.error('Error rendering Quill content:', err);
    } finally {
      setIsLoading(false);
    }
  }, [content, onHtmlGenerated]);

  if (isLoading) {
    return (
      <div 
        style={{
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '12px',
          minHeight: '100px',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...style
        }}
      >
        Loading content...
      </div>
    );
  }

  if (error) {
    return (
      <div 
        style={{
          border: '1px solid #dc3545',
          borderRadius: '4px',
          padding: '12px',
          minHeight: '100px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          ...style
        }}
      >
        Error: {error}
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`ql-editor ${className}`}
      style={{
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '12px',
        minHeight: '100px',
        backgroundColor: '#fff',
        fontFamily: 'Georgia, Times, "Times New Roman", serif',
        fontSize: '16px',
        lineHeight: '1.6',
        ...style
      }}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

export default QuillHtmlRenderer; 